import { Link } from "react-router-dom";
import { Eye, Plus } from "lucide-react";
import { useCart } from "../context/CartContext";
import { money } from "../lib/api";

export default function ProductCard({ product, onQuickView, showCompare = false }) {
  const { addItem } = useCart();
  const cat = product.categories?.[0]?.split("/").pop();
  const hasOptions = (product.options_count || 0) > 0;
  const displayPrice = product.price > 0 ? product.price : product.from_price;

  const quickAdd = () => {
    if (hasOptions) onQuickView?.(product.slug);
    else addItem(product);
  };

  return (
    <div className="group" data-testid={`product-card-${product.slug}`}>
      <div className="relative aspect-square overflow-hidden bg-brand-sand">
        <Link to={`/product/${product.slug}`} aria-label={product.name}>
          {product.image ? (
            <img
              src={product.image}
              alt={product.name}
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
            />
          ) : (
            <div className="flex h-full items-center justify-center text-xs text-neutral-400">Image coming soon</div>
          )}
        </Link>
        <div className="absolute left-3 top-3 flex flex-col gap-1.5">
          {product.on_sale && (
            <span className="rounded-full bg-brand-red px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white" data-testid="badge-sale">
              Sale
            </span>
          )}
          {!product.in_stock && (
            <span className="rounded-full bg-brand-ink px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white" data-testid="badge-sold-out">
              Sold Out
            </span>
          )}
        </div>
        <button
          onClick={() => onQuickView?.(product.slug)}
          aria-label={`Quick view ${product.name}`}
          data-testid={`quick-view-${product.slug}`}
          className="absolute right-3 top-3 rounded-full bg-white/90 p-2 opacity-0 shadow-md backdrop-blur transition-[opacity,transform] duration-200 hover:scale-110 group-hover:opacity-100"
        >
          <Eye className="h-4 w-4" />
        </button>
        {product.in_stock && (
          <button
            onClick={quickAdd}
            data-testid={`add-to-cart-${product.slug}`}
            className="absolute inset-x-3 bottom-3 hidden rounded-full bg-brand-ink py-2.5 text-xs font-bold tracking-wide text-white opacity-0 transition-[opacity,background-color,transform] duration-200 hover:bg-brand-red group-hover:opacity-100 md:block"
          >
            {hasOptions ? "SELECT OPTIONS" : "ADD TO CART"}
          </button>
        )}
      </div>
      <div className="pt-3">
        {cat && <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-neutral-400">{cat}</p>}
        <Link to={`/product/${product.slug}`}>
          <h3 className="mt-1 line-clamp-2 font-display text-[15px] font-bold leading-snug tracking-tight transition-colors hover:text-brand-red">
            {product.name}
          </h3>
        </Link>
        <div className="mt-1.5 flex items-center justify-between">
          <div className="flex items-baseline gap-2">
            <span className="text-sm font-bold" data-testid="product-price">
              {product.price > 0 ? money(displayPrice) : `From ${money(displayPrice)}`}
            </span>
            {showCompare && product.compare_at && (
              <span className="text-xs text-neutral-400 line-through">{money(product.compare_at)}</span>
            )}
          </div>
          {product.in_stock && (
            <button
              onClick={quickAdd}
              aria-label={`Add ${product.name} to cart`}
              data-testid={`mobile-add-${product.slug}`}
              className="rounded-full bg-brand-lime p-2 text-brand-ink transition-[background-color,transform] duration-200 hover:scale-110 hover:bg-brand-limehover md:hidden"
            >
              <Plus className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
